import React, { useState, useEffect, useCallback } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { UserProvider, useUserContext } from './contexts/UserContext';
import { PlayerProvider, usePlayerContext } from './contexts/PlayerContext';
import { LanguageProvider } from './contexts/LanguageContext';

import Header from './components/layout/Header';
import LeftSidebar from './components/layout/LeftSidebar';

// ── Legal pages (own layout — no app sidebar/player) ──────────────────────
import LegalLayout from './components/layout/LegalLayout';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import CopyrightPage from './pages/legal/CopyrightPage';
import GuidelinesPage from './pages/legal/GuidelinesPage';

// ── Developer docs (own layout — no app sidebar/player) ────────────────────
import DocsLayout        from './components/layout/DocsLayout';
import DocsOverviewPage  from './pages/developers/DocsOverviewPage';
import DocsAuthPage      from './pages/developers/DocsAuthPage';
import DocsBotsPage      from './pages/developers/DocsBotsPage';
import DocsGiftsPage     from './pages/developers/DocsGiftsPage';
import DocsTracksPage    from './pages/developers/DocsTracksPage';
import DocsWebhooksPage  from './pages/developers/DocsWebhooksPage';
import DocsRateLimitsPage from './pages/developers/DocsRateLimitsPage';
import DocsChangelogPage from './pages/developers/DocsChangelogPage';

import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ProfilePage from './pages/ProfilePage';
import Settings from './pages/Settings';
import UserList from './pages/UserList';
import UploadMusic from './pages/UploadMusic';
import CreateAlbum from './pages/CreateAlbum';
import PlaylistPage from './pages/PlaylistPage';
import MessagesPage from './pages/MessagesPage';
import Player from './components/player/Player';
import CreateEmojiPack from './pages/CreateEmojiPack';
import TrackPage from './pages/TrackPage';
import TagPage from './pages/TagPage';
import BottomNavBar from './components/layout/BottomNavBar';
import EmojiPacksSettings from './components/chat/EmojiPacksSettings';
import EditEmojiPack from './pages/EditEmojiPack';
import AdminRoute from './components/layout/AdminRoute';
import AdminPage from './pages/AdminPage';
import AdminUserPage from './pages/AdminUserPage';
import CreatorStudio from './pages/CreatorStudio';
import NotificationsPage from './pages/NotificationsPage';
import ReportResultPage from './pages/ReportResultPage';
import PostPage from './pages/PostPage';
import SearchPage from './pages/SearchPage';
import FollowersPage from './pages/FollowersPage';
import InAppBrowser from './components/common/InAppBrowser';
import AppsMarketplace from './pages/AppsMarketplace';
import GiftsMarketplace from './pages/GiftsMarketplace';
import ShareModal from './components/common/ShareModal';
import CloudinaryMigration from './pages/CloudinaryMigration';
import GuestPrompt from './components/common/GuestPrompt';
import StoriesRow from './components/stories/StoriesRow';
import ForgotPassword from './pages/ForgotPassword';
import NotFoundPage from './pages/NotFoundPage';
import EmailVerifiedPage from './pages/EmailVerifiedPage';
import LibraryPage from './pages/LibraryPage';
import EmailVerificationBanner from './components/common/EmailVerificationBanner';
import RoomsPage from './pages/RoomsPage';
import RoomPage from './pages/RoomPage';

import './styles/index.css';
import './components/posts/Post.css';
import './styles/App.css';
import { startLongTaskObserver } from './utils/diagnostics';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30000, // 30 seconds - prevent refetching too frequently
            cacheTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false, // Don't refetch when window regains focus
            retry: 1,
        },
    },
});

const AppLayout = () => {
    const { notification, currentTrack } = usePlayerContext();
    const { user, authLoading } = useUserContext();
    const isPlayerVisible = !!currentTrack;
    const location = useLocation();

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [browserUrl, setBrowserUrl] = useState('');
    const [isBrowserOpen, setIsBrowserOpen] = useState(false);

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [postToShare, setPostToShare] = useState(null);

    // useCallback: стабільні посилання на функції → Feed не ре-рендериться кожні 250мс
    // (PlayerContext.currentTime оновлюється кожні ~250мс під час відтворення музики,
    //  що призводить до ре-рендеру AppLayout → нові функції → Feed.React.memo не спрацьовує)
    const openBrowser = useCallback((url) => {
        setBrowserUrl(url);
        setIsBrowserOpen(true);
    }, []);

    const closeBrowser = useCallback(() => {
        setIsBrowserOpen(false);
        setBrowserUrl('');
    }, []);

    const openShareModal = useCallback((post) => {
        setPostToShare(post);
        setIsShareModalOpen(true);
    }, []);

    const closeShareModal = useCallback(() => {
        setIsShareModalOpen(false);
        setPostToShare(null);
    }, []);



    const isSidebarPage = location.pathname.startsWith('/tags/') || location.pathname === '/studio';
    const inChatView = location.pathname === '/messages' && document.body.classList.contains('in-chat-view');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const hasBottomNav = isMobile && user && !inChatView;
        if (hasBottomNav) {
            document.body.classList.add('mobile-nav-visible');
        } else {
            document.body.classList.remove('mobile-nav-visible');
        }

        if (isPlayerVisible && hasBottomNav) {
            document.body.classList.add('player-on-mobile');
        } else {
            document.body.classList.remove('player-on-mobile');
        }
    }, [isMobile, user, isPlayerVisible, inChatView]);

    // Manage all sidebar-related body classes (mode + guest fallback)
    useEffect(() => {
        const modes = ['sidebar-mode-full', 'sidebar-mode-hover', 'sidebar-mode-icons'];
        if (!authLoading && !user) {
            // Guest: no sidebar at all
            document.body.classList.add('no-sidebar');
            modes.forEach(m => document.body.classList.remove(m));
        } else if (user && !isMobile) {
            // Logged-in on desktop: apply chosen mode
            document.body.classList.remove('no-sidebar');
            const mode = user.settings?.sidebar?.mode || 'full';
            modes.forEach(m => document.body.classList.remove(m));
            document.body.classList.add(`sidebar-mode-${mode}`);
        } else {
            // Mobile or still loading: clear all sidebar classes
            modes.forEach(m => document.body.classList.remove(m));
            document.body.classList.remove('no-sidebar');
        }
    }, [authLoading, user, isMobile]);

    const isMessagesPage = location.pathname === '/messages';

    // Messages page: sidebar is hidden — reset the global padding-left offset
    useEffect(() => {
        if (isMessagesPage) {
            document.body.classList.add('no-sidebar-offset');
        } else {
            document.body.classList.remove('no-sidebar-offset');
        }
    }, [isMessagesPage]);

    return (
        <>
            <Header
                showTagPageMenu={isSidebarPage}
            />
            {/* Global sidebar — all pages except /messages and mobile */}
            {user && !isMobile && !isMessagesPage && <LeftSidebar isOpen={true} />}
            <main className="app-main-content">
                {/* Email verification banner — inside main so it never overlaps header or content */}
                {user && <EmailVerificationBanner user={user} />}
                {/* StoriesRow — тільки на головній сторінці / */}
                {user && !authLoading && location.pathname === '/' && (
                    <StoriesRow />
                )}
                <Routes>
                    <Route path="/" element={<Home openBrowser={openBrowser} openShareModal={openShareModal} />} />
                    <Route path="/apps" element={<AppsMarketplace openBrowser={openBrowser} />} />
                    <Route path="/gifts" element={<GiftsMarketplace />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/emoji-packs" element={<EmojiPacksSettings />} />
                    <Route path="/settings/emoji-packs/edit/:packId" element={<EditEmojiPack />} />
                    <Route path="/upload" element={<UploadMusic />} />
                    <Route path="/create-album" element={<CreateAlbum />} />
                    <Route path="/library" element={<LibraryPage />} />
                    <Route path="/playlist/:playlistId" element={<PlaylistPage />} />
                    <Route path="/messages" element={<MessagesPage openBrowser={openBrowser} />} />
                    <Route path="/userlist" element={<UserList />} />
                    <Route path="/create-emoji-pack" element={<CreateEmojiPack />} />
                    <Route path="/track/:trackId" element={<TrackPage />} />
                    <Route path="/tags/:tagName" element={<TagPage />} />
                    <Route path="/post/:postId" element={<PostPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/report-result/:reportId" element={<ReportResultPage />} />
                    <Route path="/studio" element={<CreatorStudio />} />
                    <Route path="/rooms" element={<RoomsPage />} />
                    <Route path="/rooms/:roomId" element={<RoomPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/email-verified" element={<EmailVerifiedPage />} />
                    <Route path="/migrate-cloudinary" element={<AdminRoute><CloudinaryMigration /></AdminRoute>} />
                    <Route
                        path="/admin"
                        element={
                            <AdminRoute>
                                <AdminPage />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/users/:uid"
                        element={
                            <AdminRoute>
                                <AdminUserPage />
                            </AdminRoute>
                        }
                    />
                    {/* Clean Twitter-style URLs — must be last (catch-all) */}
                    <Route path="/:userNickname/status/:postId" element={<PostPage openShareModal={openShareModal} />} />
                    <Route path="/:nickname/followers" element={<FollowersPage />} />
                    <Route path="/:nickname/following" element={<FollowersPage />} />
                    <Route path="/:userNickname" element={<ProfilePage openShareModal={openShareModal} />} />
                    {/* True 404 — multi-segment unknown paths */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </main>
            <BottomNavBar isPlayerVisible={isPlayerVisible} />
            <Player />

            {isBrowserOpen && <InAppBrowser initialUrl={browserUrl} onClose={closeBrowser} />}

            {isShareModalOpen && postToShare && (
                <ShareModal post={postToShare} onClose={closeShareModal} />
            )}

            {/* Guest prompt: bottom banner + 20s popup for unauthenticated users */}
            {!user && <GuestPrompt />}

            {notification.message && (
                <div className={`toast-notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}
        </>
    );
};

// ── Legal paths bypass the app layout entirely ─────────────────────────────
const LEGAL_PATHS = ['/terms', '/privacy', '/copyright', '/guidelines'];

const AppRouter = () => {
    const location = useLocation();
    if (LEGAL_PATHS.includes(location.pathname)) {
        return (
            <Routes>
                <Route path="/terms" element={<LegalLayout><TermsPage /></LegalLayout>} />
                <Route path="/privacy" element={<LegalLayout><PrivacyPage /></LegalLayout>} />
                <Route path="/copyright" element={<LegalLayout><CopyrightPage /></LegalLayout>} />
                <Route path="/guidelines" element={<LegalLayout><GuidelinesPage /></LegalLayout>} />
            </Routes>
        );
    }
    if (location.pathname.startsWith('/developers')) {
        return (
            <Routes>
                <Route path="/developers" element={<DocsLayout><DocsOverviewPage /></DocsLayout>} />
                <Route path="/developers/authentication" element={<DocsLayout><DocsAuthPage /></DocsLayout>} />
                <Route path="/developers/bots" element={<DocsLayout><DocsBotsPage /></DocsLayout>} />
                <Route path="/developers/gifts" element={<DocsLayout><DocsGiftsPage /></DocsLayout>} />
                <Route path="/developers/tracks" element={<DocsLayout><DocsTracksPage /></DocsLayout>} />
                <Route path="/developers/webhooks" element={<DocsLayout><DocsWebhooksPage /></DocsLayout>} />
                <Route path="/developers/rate-limits" element={<DocsLayout><DocsRateLimitsPage /></DocsLayout>} />
                <Route path="/developers/changelog" element={<DocsLayout><DocsChangelogPage /></DocsLayout>} />
            </Routes>
        );
    }
    return <AppLayout />;
};

const AppContent = () => (
    <Router>
        <AppRouter />
    </Router>
);

const App = () => {
    // Запускаємо спостерігач за довгими задачами один раз
    useEffect(() => {
        startLongTaskObserver();
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <LanguageProvider>
                <UserProvider>
                    <PlayerProvider>
                        <AppContent />
                        <Analytics />
                    </PlayerProvider>
                </UserProvider>
            </LanguageProvider>
        </QueryClientProvider>
    );
};

export default App;